import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectOrganisationGateways,
  selectOrganisationSites,
} from '../../../../../redux/selectors/organisation';

import AddOrEditGateway from './AddOrEditGateway';

import {
  doDeleteOrganisationGateway,
  doReadOrganisationGateways,
} from '../../../../../redux/thunks/organisation';
import { selectCurrentPartner } from '../../../../../redux/selectors/authentication';
import { useParams } from 'react-router-dom';

import Table from '../../../../../components/Table/Table';

import SquareDelete from '../../../../../components/SquareDelete/SquareDelete';
import { statusMapping, textStatusRender } from '../../../../../helpers/renderers';
import '../../../../../styles/common.scss';
import Button from '../../../../../components/Buttons/Button';
import ModalAlert from '../../../../../components/Modals/ModalAlert';
import { Search, SearchStatus } from '../../../../../utils/Search';
import { meshConfiguredStateOptions, meshConfiguredStateRender } from '../../../../../utils/Common';
import useIsMobile from '../../../../../hooks/useIsMobile';
import MobileTable from '../../../../../components/MobileTable/MobileTable';
import SDialog from '../../../../../components/SDialog/SDialog';
import Typography from '../../../../../components/Typography/Typography';
import FlexRow from '../../../../../components/FlexBox/FlexRow';
import SButton from '../../../../../components/SButton/SButton';

const GatewayTab = () => {

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [, setPopupTitle] = useState('Add');
  const [showGatewayPopup, setShowGatewayPopup] = useState(false);
  const [showDeleteGatewayPopup, setShowDeleteGatewayPopup] = useState(false);
  const [gateway, setGateway] = useState(null);
  const { customerId } = useParams();
  const [filters, setFilters] = useState({
    serialNumber: '',
    cloudStatus: '',
    siteName: '',
    networkName: '',
    resourceState: '',
  });
  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 7 });

  const { gateways, currentUser, sites } = useSelector((state) => ({
    gateways: selectOrganisationGateways(state),
    sites: selectOrganisationSites(state),
    currentUser: selectCurrentPartner(state),
  }));

  const columns = useMemo(() => {
    const commonColumns = [
      {
        title: 'Gateway Serial Number',
        field: 'serialNumber',
        name: 'serialNumber',
      },
      {
        title: 'Site',
        field: 'siteName',
        name: 'siteName',
      },
      {
        title: 'Network',
        name: 'networkName',
        field: 'networkName',
      },
      {
        title: 'State',
        field: 'configurationStatus',
        name: 'configurationStatus',
        lookup: meshConfiguredStateOptions,
        render: (columnProps, row) => {
          return meshConfiguredStateRender(row.configurationStatus);
        },
        renderM: (columnProps, row) => {
          return meshConfiguredStateRender(row.configurationStatus);
        },
      },
      {
        title: 'Status',
        field: 'cloudStatus',
        name: 'cloudStatus',
        render: (columnProps, row) => {
          return textStatusRender(row.connectivityStatus);
        },
        renderM: (columnProps, row) => {
          return textStatusRender(row.connectivityStatus);
        },
      }
    ]

    const actions = [
      {
        title: 'Action',
        render: (columnProps, row) => {
          return (
            <SquareDelete
              color={'#ff535a'}
              onPress={() => {
                setPopupTitle('Delete');
                setGateway(row);
                togglePopup('DeleteGateway');
              }}
              style={{ width: '2rem', height: '2rem', padding: '0' }}
            />
          );
        },
        renderM: (columnProps, row) => {
          return (
            <SquareDelete
              color={'#ff535a'}
              onPress={() => {
                setPopupTitle('Delete');
                setGateway(row);
                togglePopup('DeleteGateway');
              }}
              style={{ width: '2rem', height: '2rem', padding: '0' }}
            />
          );
        },
      }
    ]

    return {
      allColumns: [...commonColumns, ...actions],
      commonColumns: commonColumns,
      actions: actions
    }

  }, []);

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Serial Number", key: "serialNumber", type: "search" },
      { name: "Site Name", key: "siteName", type: "search" },
      { name: "Network", key: "networkName", type: "search" },
      { name: "State", key: "configurationStatus", type: "select", options: meshConfiguredStateOptions },
      { name: "Status", key: "cloudStatus", type: "search" },
    ]
  }, [])

  const updatedGateways = useMemo(
    () =>
      gateways?.map((currentGateway) => {
        const foundSite = sites.find((site) => site.id === currentGateway.siteId);
        return { ...currentGateway, formattedStatus: statusMapping(currentGateway?.connectivityStatus), site: foundSite };
      }),
    [gateways, sites],
  );

  const togglePopup = (popupName) => {
    if (popupName === 'AddOrEditGateway') {
      setShowGatewayPopup(!showGatewayPopup);
      if (showGatewayPopup) {
        setGateway(null);
      }
    }

    if (popupName === 'DeleteGateway') {
      setShowDeleteGatewayPopup(!showDeleteGatewayPopup);
      if (showDeleteGatewayPopup) {
        setGateway(null);
      }
    }
  };

  const handleDelete = (e) => {
    dispatch(
      doDeleteOrganisationGateway({
        partnerId: currentUser.partnerId,
        organisationId: customerId,
        serialNumber: gateway.serialNumber,
      }),
    );
    setGateway(null);
    setShowDeleteGatewayPopup(false);
  };

  useEffect(() => {
    if (currentUser)
      dispatch(
        doReadOrganisationGateways({
          partnerId: currentUser.partnerId,
          organisationId: customerId,
        }),
      );
  }, [dispatch, currentUser, customerId]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: updatedGateways?.length ? updatedGateways?.length : 0
    }))
  }, [updatedGateways?.length, setPagination]);

  const filteredData = useCallback(() => {
    let data = updatedGateways ? updatedGateways : [];
    if (filters.serialNumber !== '') {
      data = Search(data, 'serialNumber', filters.serialNumber);
    }
    if (filters.cloudStatus !== '') {
      data = Search(data, 'formattedStatus', filters.cloudStatus);
    }
    if (filters.siteName !== '') {
      data = Search(data, 'siteName', filters.siteName);
    }
    if (filters.networkName !== '') {
      data = Search(data, 'networkName', filters.networkName);
    }
    if (filters?.configurationStatus?.id) {
      data = data?.filter(d => d?.configurationStatus == Number(filters?.configurationStatus?.id)) || []
    }

    if (!isMobile) {
      data = data.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    }

    return data;
  }, [
    filters.cloudStatus,
    filters.serialNumber,
    filters.siteName,
    filters.resourceState,
    filters?.configurationStatus?.id,
    filters.networkName,
    pagination.currentPage,
    pagination.perPage,
    updatedGateways,
    isMobile
  ]);

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  };

  const search = {
    onChange: hSearchChange,
  };

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page
    }))
  };

  return (
    <Fragment>
      <div className="spacer__1" />
      <div>
        {showGatewayPopup && (
          <AddOrEditGateway
            {...{
              showGatewayPopup,
              onClose: () => togglePopup('AddOrEditGateway'),
              gateway,
              togglePopup,
            }}
          />
        )}
        {showDeleteGatewayPopup && (
          <SDialog
            title='Delete Gateway'
            open={showDeleteGatewayPopup}
            onClose={() => {
              setShowDeleteGatewayPopup(false);
            }}
            secondaryButtonConfig={{
              text: 'Cancel',
              onClick: () => {
                setShowDeleteGatewayPopup(false);
              }
            }}
            primaryButtonConfig={{
              text: 'Delete',
              color: 'error',
              onClick: handleDelete
            }}
          >
            <Typography variant={"b1M"} style={{ padding: '1.5rem 0' }}>Are you sure you want to delete Gateway {gateway ? gateway.serialNumber : ''} ? This action is
              non reversible and will cause you to lose all data associated the Gateway.</Typography>
          </SDialog>
        )}
        <div>
          <FlexRow justifyContent='space-between' styles={{ marginBottom: '1rem' }}>
            <Typography variant={"h7M"}>Gateways</Typography>
            <SButton
              minWidth
              onClick={() => {
                setPopupTitle('Add');
                togglePopup('AddOrEditGateway');
              }}
              disabled={sites == null || sites.length === 0}
            >
              Add Gateway
            </SButton>
          </FlexRow>

          {isMobile ?
            <MobileTable
              columns={columns?.commonColumns}
              data={filteredData() || []}
              actions={columns?.actions}
              filters={filters}
              setFilters={setFilters}
              filtersConfig={mobileFiltersConfig}
              emptyMessage={'No Gateways Found'}
            />
            : <Table
              columns={columns?.allColumns}
              data={filteredData() || []}
              pagination={pagination}
              defaultMessage={'No Gateways Found'}
              options={{ search }}
              changePage={changePage}
            />}
        </div>
      </div>
    </Fragment>
  );
};

export default GatewayTab;
